import React, { useState, useEffect, useRef, useCallback } from 'react';
import { COLORS } from './constants.js';
import StyleTag from './StyleTag.jsx';
import ChangeReasonPrompt from './ChangeReasonPrompt.jsx';
import AiLearningPrompt from './AiLearningPrompt.jsx';
import AiLearningProgress from './AiLearningProgress.jsx';

// ScoreScale component - A scale-based grading system inspired by Google Forms
const ScoreScale = ({
  subQuestion,
  gradeRatings,
  showThumbsDownMessage,
  handleThumbsUp,
  handleThumbsDown,
  handleManualGradeChange,
  getFeedback,
  handleRubricItemChange,
  editingSolutionId,
  setEditingCriteriaId,
  setChangedMaxWeights,
  renderedQuestions,
  setRenderedQuestions,
  showChangeReasonPrompt,
  changeReasons,
  handleReasonChange,
  handleChangeReasonSubmit,
  handleChangeReasonSkip,
  showAiLearningPrompt,
  aiLearningLoading,
  triggerAiLearning,
  handleAiLearningDismiss,
  aiLearningInProgress,
  triggerAiLearningWithPolling
}) => {
  // Register the element in refs system when it mounts
  useEffect(() => {
    const element = document.getElementById(`subquestion-${subQuestion.id}`);
    if (element) {
      // Note: rubricItemRefs is passed through props, assumed to be a ref object
    }
  }, [subQuestion.id]);

  // Find the max possible score from the max_weight of the subquestion
  const initialMaxScore = subQuestion.max_weight || 0;
  const [maxScore, setMaxScore] = useState(initialMaxScore);
  const simplifyScale = maxScore > 8; // Threshold for simplifying

  // Get the student-specific feedback using the prop function
  const studentFeedback = getFeedback(subQuestion.id);

  // Determine the score to display, prioritizing the student-specific feedback score
  let displayScore = 0;
  if (studentFeedback) {
    displayScore = parseFloat(studentFeedback.score);
    // Ensure we handle NaN correctly, default to 0 if parsing fails
  }

  // === NEW STATE for editing ===
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [editValue, setEditValue] = useState(displayScore.toString()); // Store input value as string
  const [isEditingMaxScore, setIsEditingMaxScore] = useState(false);
  const [editMaxValue, setEditMaxValue] = useState(maxScore.toString());
  const [tempMaxScore, setTempMaxScore] = useState(maxScore); // Store the temporary edited max score

  // Update editValue if displayScore changes externally (e.g., via slider click)
  useEffect(() => {
    if (!isEditingScore) {
      setEditValue(displayScore.toString());
    }
  }, [displayScore, isEditingScore]);

  // Initialize maxScore from subQuestion.max_weight when the component mounts or when it changes significantly
  useEffect(() => {
    // Only update if not in editing mode
    if (!editingSolutionId || editingSolutionId !== subQuestion.id) {
      setMaxScore(initialMaxScore);
      setEditMaxValue(initialMaxScore.toString());
      setTempMaxScore(initialMaxScore);
    }
  }, [initialMaxScore, subQuestion.id, editingSolutionId]);

  // Make max score editable when fine tune is clicked
  useEffect(() => {
    if (editingSolutionId === subQuestion.id) {
      // Don't automatically set isEditingMaxScore to true when editingSolutionId changes
      // This way, max score editing only starts when explicitly clicked

      // But DO load the stored value from the data attribute if it exists
      const element = document.getElementById(`subquestion-${subQuestion.id}`);
      if (element) {
        const dataMaxWeight = element.getAttribute('data-max-weight');
        if (dataMaxWeight) {
          const parsedValue = parseFloat(dataMaxWeight);
          if (!isNaN(parsedValue) && parsedValue >= 0) {
            // Update all relevant state variables to ensure consistency
            setMaxScore(parsedValue);
            setEditMaxValue(parsedValue.toString());
            setTempMaxScore(parsedValue);
          }
        }
      }
    }
  }, [editingSolutionId, subQuestion.id]);

  // Create score points - adapt increment based on maxScore
  const scorePoints = [];
  const increment = simplifyScale ? 1 : 0.5; // Increment by 1 if maxScore > 8

  // Use tempMaxScore for calculating points instead of maxScore
  const effectiveMaxScore = editingSolutionId === subQuestion.id ? tempMaxScore : maxScore;
  for (let i = 0; i <= effectiveMaxScore; i += increment) {
    scorePoints.push(i);
  }

  // Reference for drag handling
  const sliderRef = useRef(null);
  const [hoverScore, setHoverScore] = useState(null); // Track score being hovered for visual feedback

  // Simple click handler for dots - just update the score
  const handleDotClick = score => {
    // Always show reasoning prompt when grade is clicked manually
    handleManualGradeChange(subQuestion.id);

    // Update the backend - This function (passed from parent) should handle local state update
    if (handleRubricItemChange) {
      handleRubricItemChange(subQuestion.id, score)();
    }
  };

  // Just handle hover over the slider - no drag functionality
  const handleSliderHover = e => {
    if (!sliderRef.current) {
      return;
    }

    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const score = Math.round(percentage * maxScore * 2) / 2; // Round to nearest 0.5

    setHoverScore(score);
  };

  // Simple click handler for the slider
  const handleSliderClick = e => {
    if (!sliderRef.current) {
      return;
    }

    // Calculate score from click position
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const score = Math.round(percentage * maxScore * 2) / 2; // Round to nearest 0.5

    // Use the same click handler as the dots
    handleDotClick(score);
  };

  // Handle mouse leave to clear hover state
  const handleMouseLeave = () => {
    setHoverScore(null);
  };

  // === NEW HANDLERS for input ===
  const handleScoreClick = () => {
    setEditValue(displayScore.toString()); // Initialize with current score
    setIsEditingScore(true);
    // Maybe focus the input shortly after render? (Requires ref)
  };

  const handleInputChange = e => {
    setEditValue(e.target.value);
  };

  const saveEditedScore = () => {
    let newScore = parseFloat(editValue);
    if (isNaN(newScore)) {
      newScore = displayScore; // Revert if input is not a number
    } else {
      // Clamp the score
      // === UPDATED: Round to max 2 decimal places ===
      newScore = Math.round(newScore * 100) / 100;
    }
    setIsEditingScore(false);

    // Always show reasoning prompt when grade is manually entered
    handleManualGradeChange(subQuestion.id);

    // Update the backend - This function (passed from parent) should handle local state update
    if (handleRubricItemChange) {
      handleRubricItemChange(subQuestion.id, newScore)();
    }
    // Reset editValue in case clamping/rounding occurred
    setEditValue(newScore.toString());
  };

  const handleInputBlur = () => {
    saveEditedScore();
  };

  const handleInputKeyDown = e => {
    if (e.key === 'Enter') {
      saveEditedScore();
    } else if (e.key === 'Escape') {
      setEditValue(displayScore.toString()); // Revert to original score
      setIsEditingScore(false);
    }
  };

  // === NEW HANDLERS for max score input ===
  const handleMaxScoreClick = () => {
    // Only allow editing if we're in fine-tune mode (editingSolutionId === subQuestion.id)
    if (editingSolutionId === subQuestion.id) {
      // Clear criteria editing state to prevent focus issues
      setEditingCriteriaId(null);
      setEditMaxValue(maxScore.toString()); // Initialize with current max score
      setIsEditingMaxScore(true);
    }
  };

  const handleMaxInputChange = e => {
    setEditMaxValue(e.target.value);
  };

  const saveEditedMaxScore = () => {
    let newMaxScore = parseFloat(editMaxValue);
    // Round to max 2 decimal places
    newMaxScore = Math.round(newMaxScore * 100) / 100;

    setIsEditingMaxScore(false);

    // Update our states to maintain the new value
    if (newMaxScore !== tempMaxScore) {
      setTempMaxScore(newMaxScore);
      setMaxScore(newMaxScore);
      setEditMaxValue(newMaxScore.toString());

      // Store the value in a data attribute on the element for retrieval during regrade
      const element = document.getElementById(`subquestion-${subQuestion.id}`);
      if (element) {
        element.setAttribute('data-max-weight', newMaxScore.toString());
      }
    }

    // Even if the value hasn't changed, still update the data attribute to ensure consistency
    const element = document.getElementById(`subquestion-${subQuestion.id}`);
    if (element) {
      element.setAttribute('data-max-weight', newMaxScore.toString());
    }
  };

  const handleMaxInputBlur = () => {
    saveEditedMaxScore();
  };

  const handleMaxInputKeyDown = e => {
    if (e.key === 'Enter') {
      saveEditedMaxScore();
    } else if (e.key === 'Escape') {
      setEditMaxValue(maxScore.toString()); // Revert to original max score
      setIsEditingMaxScore(false);
    }
  };

  // Function to determine color based on the score
  const getColorForScore = score => {
    const percentage = score / maxScore;

    if (percentage >= 1) {
      // Fully correct: premium gold/green gradient
      return {
        color: COLORS.green,
        solidColor: COLORS.green,
        gradient: `linear-gradient(to right, ${COLORS.green}, ${COLORS.green})`,
        lightGradient: `linear-gradient(to right, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.3))`,
        shadowColor: 'rgba(16, 185, 129, 0.4)'
      };
    } else {
      // Partially correct: amber/gold gradient
      return {
        color: COLORS.primary,
        solidColor: COLORS.primary,
        gradient: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.primaryDark})`,
        lightGradient: `linear-gradient(to right, rgba(228, 177, 122, 0.15), rgba(201, 155, 105, 0.3))`,
        shadowColor: 'rgba(228, 177, 122, 0.4)'
      };
    }
  };

  const colors = getColorForScore(displayScore);

  return (
    <div
      // Reduce padding from p-6 to p-4
      className="scale-container p-4 rounded-xl border border-gray-200 bg-white shadow-sm animate-scale-entrance transition-all duration-300"
      style={{
        background: `linear-gradient(to bottom, white, ${COLORS.bgLight})`
      }}
    >
      <StyleTag />

      {/* Score display header - better centered and with higher contrast */}
      <div className="mb-3 flex justify-center items-center">
        <div className="flex items-baseline gap-2 justify-center text-center">
          <div
            className="text-3xl font-bold flex items-baseline"
            style={{ color: COLORS.textPrimary }}
          >
            {/* === CONDITIONAL RENDERING for score === */}
            {isEditingScore ? (
              <input
                type="number"
                value={editValue}
                className="box-border hide-number-spinners font-bold text-center align-baseline outline-none border-b-2 border-amber-500 bg-transparent rounded-md px-1 py-0 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-500 leading-none"
                style={{
                  color: colors.solidColor,
                  width: `${maxScore.toString().length + 3}ch`, // May need adjustment for larger font
                  outline: 'none !important',
                  fontSize: '1.9rem', // Explicitly EVEN larger font size (like text-8xl)
                  lineHeight: '1'
                }}
                step="0.5"
                min="0"
                max={maxScore}
                autoFocus // Focus when it appears
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
              />
            ) : (
              <span
                className="text-6xl cursor-pointer hover:opacity-80 transition-opacity" // Increased size
                style={{ color: colors.solidColor }}
                onClick={handleScoreClick} // Click to edit
              >
                {displayScore}
              </span>
            )}
            {/* === END CONDITIONAL RENDERING === */}
            <span className="mx-1 text-2xl text-gray-600">/</span>
            {/* === CONDITIONAL RENDERING for max score === */}
            {isEditingMaxScore && editingSolutionId === subQuestion.id ? (
              <input
                type="number"
                value={editMaxValue}
                className="box-border hide-number-spinners font-bold text-center align-baseline outline-none border-b-2 border-amber-500 bg-transparent rounded-md px-1 py-0 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-500 leading-none"
                style={{
                  color: '#8c6b4a',
                  width: `${editMaxValue.length + 3}ch`,
                  outline: 'none !important',
                  fontSize: '1.9rem',
                  lineHeight: '1'
                }}
                step="0.5"
                min="0"
                max="20"
                autoFocus
                onChange={handleMaxInputChange}
                onBlur={handleMaxInputBlur}
                onKeyDown={handleMaxInputKeyDown}
              />
            ) : (
              <div className="flex items-center">
                <span
                  className={`text-2xl ${editingSolutionId === subQuestion.id ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  style={{
                    textDecoration:
                      editingSolutionId === subQuestion.id ? 'underline dotted' : 'none',
                    textDecorationColor:
                      editingSolutionId === subQuestion.id ? 'rgba(228, 177, 122, 0.5)' : 'none'
                  }}
                  onClick={handleMaxScoreClick} // Click handler will check if editing is allowed
                >
                  {maxScore}
                </span>
              </div>
            )}
            {/* === END CONDITIONAL RENDERING === */}
            <span className="ml-2 text-base font-medium text-gray-700">points</span>
          </div>
        </div>

        {/* Thumbs up/down rating buttons - positioned slightly to the right */}
        <div className="flex items-center gap-3 ml-8">
          <button
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 shadow-sm border-2 ${
              aiLearningInProgress[subQuestion.id]
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                : gradeRatings[subQuestion.id] === 'up'
                ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400 shadow-green-200'
                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700'
            }`}
            title={aiLearningInProgress[subQuestion.id] ? "Disabled during AI learning" : "Rate this grade as good"}
            onClick={aiLearningInProgress[subQuestion.id] ? undefined : () => handleThumbsUp(subQuestion.id)}
            disabled={aiLearningInProgress[subQuestion.id]}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
            </svg>
          </button>

          <button
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 shadow-sm border-2 ${
              aiLearningInProgress[subQuestion.id]
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                : gradeRatings[subQuestion.id] === 'down'
                ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 hover:border-red-400 shadow-red-200'
                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400 hover:text-gray-700'
            }`}
            title={aiLearningInProgress[subQuestion.id] ? "Disabled during AI learning" : "Rate this grade as incorrect"}
            onClick={aiLearningInProgress[subQuestion.id] ? undefined : () => handleThumbsDown(subQuestion.id)}
            disabled={aiLearningInProgress[subQuestion.id]}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.057 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Thumbs down message */}
      {showThumbsDownMessage[subQuestion.id] && (
        <div className="mb-3 p-4 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 rounded-lg shadow-sm">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-red-700 text-base font-semibold">
                Please select the correct grade using the scale below
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Change reason prompt */}
      <ChangeReasonPrompt
        key={`change-reason-${subQuestion.id}`}
        subQuestionId={subQuestion.id}
        isVisible={showChangeReasonPrompt[subQuestion.id]}
        reason={changeReasons[subQuestion.id] || ''}
        onReasonChange={handleReasonChange}
        onSubmit={handleChangeReasonSubmit}
        onSkip={handleChangeReasonSkip}
      />

      {/* AI Learning prompt */}
      <AiLearningPrompt
        subQuestionId={subQuestion.id}
        isVisible={showAiLearningPrompt[subQuestion.id]}
        isLoading={aiLearningLoading[subQuestion.id]}
        onTriggerLearning={(subQuestionId) => triggerAiLearning(subQuestionId, triggerAiLearningWithPolling)}
        onDismiss={() => handleAiLearningDismiss(subQuestion.id)}
      />

      {/* AI Learning progress */}
      <AiLearningProgress
        subQuestionId={subQuestion.id}
        isVisible={aiLearningInProgress[subQuestion.id]}
      />

      {/* Score scale slider */}
      {/* Reduce height from h-20 to h-16, remove mt-1 */}
      <div
        ref={sliderRef}
        className={`relative h-16 flex items-center mt-0 ${
          aiLearningInProgress[subQuestion.id]
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer'
        }`}
        onClick={aiLearningInProgress[subQuestion.id] ? undefined : handleSliderClick}
        onMouseMove={aiLearningInProgress[subQuestion.id] ? undefined : handleSliderHover}
        onMouseLeave={aiLearningInProgress[subQuestion.id] ? undefined : handleMouseLeave}
      >
        {/* Track background with subtle gradient */}
        <div
          className="absolute h-2 w-full rounded-full top-1/2 transform -translate-y-1/2"
          style={{
            background: COLORS.lightGray,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
          }}
        ></div>

        {/* Filled portion of the track */}
        <div
          className="absolute h-2 rounded-full scale-fill top-1/2 transform -translate-y-1/2"
          style={{
            width: `${(displayScore / maxScore) * 100}%`,
            background: colors.gradient,
            boxShadow: `0 1px 3px ${colors.shadowColor}`
          }}
        ></div>

        {/* Improved tooltip with better visibility and contrast */}
        {hoverScore !== null && hoverScore !== displayScore && (
          <div
            className="absolute"
            style={{
              left: `${(hoverScore / maxScore) * 100}%`,
              bottom: '-32px',
              transform: 'translateX(-50%)',
              width: '46px',
              height: '28px',
              zIndex: 50
            }}
          >
            <div
              className="mx-auto text-center bg-white rounded-md px-3 py-1 shadow-md"
              style={{
                background: COLORS.bgWhite,
                color: getColorForScore(hoverScore).solidColor,
                border: `1.5px solid ${getColorForScore(hoverScore).solidColor}`,
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: 1.2,
                width: 'fit-content'
              }}
            >
              {hoverScore}
            </div>
          </div>
        )}

        {/* Hover indicator */}
        {hoverScore !== null && (
          <div
            className="absolute w-4 h-4 rounded-full bg-white shadow-md transform -translate-x-1/2 -translate-y-1/2 z-20"
            style={{
              left: `${(hoverScore / maxScore) * 100}%`,
              border: `2px solid ${getColorForScore(hoverScore).solidColor}`,
              top: '50%',
              transition: 'left 0.1s ease'
            }}
          ></div>
        )}

        {/* The score points (dots) with invisible larger click areas */}
        {scorePoints.map((score, index) => {
          const isSelected = score <= displayScore;
          // Use whole number check for dot size consistency
          const isWholeNumber = score % 1 === 0;
          const isCurrentScore = score === displayScore;

          // Reduce whole number dot size but keep it larger than half-point dots
          const dotSize = isWholeNumber ? 'w-6 h-6' : 'w-3 h-3';

          // Add a large invisible click area that doesn't affect visuals
          const clickAreaSize = 'w-10 h-10';

          return (
            <div
              key={index}
              className={`scale-dot absolute transform -translate-x-1/2 -translate-y-1/2 ${clickAreaSize} cursor-pointer z-10 flex items-center justify-center`}
              style={{
                left: `${(score / maxScore) * 100}%`,
                top: '50%'
              }}
              onClick={() => {
                // Simple direct call to handleDotClick
                handleDotClick(score);
              }}
              onMouseEnter={() => setHoverScore(score)}
              onMouseLeave={() => setHoverScore(null)}
            >
              <div
                className={`${dotSize} rounded-full flex items-center justify-center`}
                style={{
                  backgroundColor: isSelected ? colors.solidColor : '#fff',
                  border: `2px solid ${isSelected ? colors.solidColor : COLORS.gray}`,
                  // Apply transparent shadow when not current to reserve space
                  boxShadow: isCurrentScore
                    ? `0 0 0 2px ${colors.shadowColor}`
                    : '0 0 0 2px transparent',
                  transition:
                    'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease'
                }}
              >
                {/* Display numbers inside the dots for whole numbers */}
                {isWholeNumber && (
                  <span
                    className="text-sm font-bold"
                    style={{
                      color: isSelected ? 'white' : colors.solidColor
                    }}
                  >
                    {score}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreScale;